import { Component, EventEmitter, Output } from '@angular/core';
import { TRANSACTION_STATUS_OPTIONS, TRANSACTION_ON_US_OPTIONS, TRANSACTION_PARTY_TYPE_OPTIONS, TRANSACTION_PRODUCT_CODE_OPTIONS } from '../../../config/transaction-report.config';

export interface IFilterCriterion {
  field: string;
  operator: string;
  value: string;
  valueEnd?: string;
  valueList?: string[];
}

@Component({
  selector: 'app-transaction-filter',
  templateUrl: './transaction-filter.component.html',
  styleUrls: ['./transaction-filter.component.scss']
})
export class TransactionFilterComponent {
  @Output() search = new EventEmitter<string[]>();
  @Output() reset = new EventEmitter<void>();

  public criteria: IFilterCriterion[] = [];
  public fields = [
    { value: 'txnId', label: 'Transaction ID' },
    { value: 'externalId', label: 'External ID' },
    { value: 'status', label: 'Status' },
    { value: 'currency', label: 'Currency' },
    { value: 'amount', label: 'Amount' },
    { value: 'createDate', label: 'Create Date' },
    { value: 'senderName', label: 'Sender Name' },
    { value: 'senderMobileNumber', label: 'Sender Mobile' },
    { value: 'receiverName', label: 'Receiver Name' },
    { value: 'receiverMobileNumber', label: 'Receiver Mobile' },
    { value: 'productCode18n', label: 'Transaction Type' },
    { value: 'transactionType', label: 'Credit/Debit' },
    { value: 'onUs', label: 'On Us' },
    { value: 'senderType', label: 'Sender Type' },
    { value: 'receiverType', label: 'Receiver Type' }
  ];

  public operators = [
    { value: 'eq', label: 'Equals' },
    { value: 'ne', label: 'Not Equals' },
    { value: 'gt', label: 'Greater Than' },
    // { value: 'gte', label: 'Greater Than or Equal' },
    { value: 'lt', label: 'Less Than' },
    // { value: 'lte', label: 'Less Than or Equal' },
    { value: 'in', label: 'In list' },
    { value: 'nin', label: 'Not In list' },
    // { value: 'contains', label: 'Contains' },
    // { value: 'startswith', label: 'Starts With' },
    // { value: 'between', label: 'Between' }
  ];

  public statusOptions = TRANSACTION_STATUS_OPTIONS;

  public currencyOptions = [
    { label: 'MAD', value: 'MAD' },
    { label: 'USD', value: 'USD' },
    { label: 'EUR', value: 'EUR' }
  ];

  public txTypeOptions = [
    { label: 'CREDIT', value: 'CREDIT' },
    { label: 'DEBIT', value: 'DEBIT' }
  ];

  public onUsOptions = TRANSACTION_ON_US_OPTIONS;

  public partyTypeOptions = TRANSACTION_PARTY_TYPE_OPTIONS;

  public productCodeOptions = TRANSACTION_PRODUCT_CODE_OPTIONS;

  public fixedFilters = {
    createDateFrom: '',
    createDateTo: '',
    status: '',
    productCode18n: '',
    senderType: '',
    receiverType: ''
  };

  constructor() {
    // Add one default filter criterion on initialization
    this.addCriterion();
  }

  getPlaceholder(criterion: IFilterCriterion): string {
    return 'Enter value...';
  }

  isInOperator(operator: string): boolean {
    return operator === 'in' || operator === 'nin';
  }

  getOptionsForField(field: string): { label: string; value: string }[] | null {
    if (field === 'status') {
      return this.statusOptions;
    }
    if (field === 'currency') {
      return this.currencyOptions;
    }
    if (field === 'transactionType') {
      return this.txTypeOptions;
    }
    if (field === 'onUs') {
      return this.onUsOptions;
    }
    if (field === 'senderType' || field === 'receiverType') {
      return this.partyTypeOptions;
    }
    if (field === 'productCode18n') {
      return this.productCodeOptions;
    }
    return null;
  }

  addCriterion(): void {
    this.criteria.push({
      field: 'txnId',
      operator: 'eq',
      value: '',
      valueEnd: '',
      valueList: []
    });
  }

  removeCriterion(index: number): void {
    this.criteria.splice(index, 1);
    if (this.criteria.length === 0) {
      this.addCriterion();
    }
  }

  onFieldChange(index: number): void {
    this.criteria[index].value = '';
    this.criteria[index].valueEnd = '';
    this.criteria[index].valueList = [];

    const field = this.criteria[index].field;
    if (field === 'status' || field === 'currency' || field === 'transactionType' || field === 'onUs' || field === 'senderType' || field === 'receiverType' || field === 'productCode18n') {
      this.criteria[index].operator = 'eq';
    } else if (field === 'amount') {
      this.criteria[index].operator = 'eq';
    } else if (field === 'createDate') {
      this.criteria[index].operator = 'gte';
    } else {
      this.criteria[index].operator = 'contains';
    }
  }

  onOperatorChange(index: number): void {
    this.criteria[index].value = '';
    this.criteria[index].valueEnd = '';
    this.criteria[index].valueList = [];
  }

  onSearch(): void {
    const filterExpressions: string[] = [];

    if (this.fixedFilters.createDateFrom) {
      filterExpressions.push(`createDate:gte:${this.fixedFilters.createDateFrom}`);
    }
    if (this.fixedFilters.createDateTo) {
      filterExpressions.push(`createDate:lte:${this.fixedFilters.createDateTo}`);
    }
    if (this.fixedFilters.status) {
      filterExpressions.push(`status:eq:${this.fixedFilters.status}`);
    }
    if (this.fixedFilters.productCode18n) {
      filterExpressions.push(`productCode18n:eq:${this.fixedFilters.productCode18n}`);
    }
    if (this.fixedFilters.senderType) {
      filterExpressions.push(`senderType:eq:${this.fixedFilters.senderType}`);
    }
    if (this.fixedFilters.receiverType) {
      filterExpressions.push(`receiverType:eq:${this.fixedFilters.receiverType}`);
    }

    this.criteria.forEach(c => {
      if (c.field && c.operator) {
        const val = c.value !== undefined ? c.value.toString().trim() : '';

        if (c.operator === 'in' || c.operator === 'nin') {
          const list = (c.valueList || []).map(v => v.toString().trim()).filter(v => v);
          if (list.length) {
            filterExpressions.push(`${c.field}:${c.operator}:${list.join(',')}`);
          }
        } else if (c.operator === 'between') {
          const valEnd = c.valueEnd !== undefined ? c.valueEnd.toString().trim() : '';
          if (val && valEnd) {
            filterExpressions.push(`${c.field}:between:${val},${valEnd}`);
          }
        } else if (val) {
          filterExpressions.push(`${c.field}:${c.operator}:${val}`);
        }
      }
    });

    this.search.emit(filterExpressions);
  }

  onReset(): void {
    this.criteria = [];
    this.fixedFilters = {
      createDateFrom: '',
      createDateTo: '',
      status: '',
      productCode18n: '',
      senderType: '',
      receiverType: ''
    };
    this.addCriterion();
    this.reset.emit();
  }
}
